import { useFetch } from "hooks/fetch/useFetch";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Provides a callback function which can be used to execute a mutation an existing object in the api
 * @param url the url to do the mutation on
 * @param method the request type - should be POST, PUT or DELETE
 * @param onCompleted callback that is fired when the mutation completes successfully
 * @param onError callback that is fired when the mutation errors
 * @returns {{fetchCallback: ((function(*=, *=): void)|*), loading: boolean, error: boolean}}
 */
export function useLazyFetch(
  url,
  method,
  onCompleted = undefined,
  onError = undefined
) {
  const [{ loading, error }, setFetchState] = useState({
    loading: false,
    error: false
  });

  const abortController = useMemo(() => {
    return new AbortController();
  }, []);

  // abort the request when the component using the hook is unmounted
  useEffect(() => {
    return () => abortController.abort();
  }, [abortController]);

  const { callbackFunction } = useFetch(url);

  const fetchCallback = useCallback(
    (body, headers = { "Content-Type": "application/json" }) => {
      setFetchState({ loading: true, error: false });
      const jsonBody = JSON.stringify(body);
      callbackFunction({
        method: method,
        body: jsonBody,
        signal: abortController.signal,
        headers
      }).then(
        response => {
          setFetchState({ loading: false, error: false });
          onCompleted && onCompleted(response);
        },
        error => {
          setFetchState({ loading: false, error: true });
          onError && onError(error);
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abortController.signal, callbackFunction, method]
  );

  return { loading, error, fetchCallback };
}
